'use client';

import { useState, useEffect, useCallback } from 'react';
import { Calculator, DollarSign, Clock, TrendingUp, Info } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { savingsCalculatorContent } from '../../data/savings-calculator-content';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData
} from 'chart.js';
import type { ChartData as ChartDataType } from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type TimePeriod = 'years' | 'months';

interface SavingsData {
  principal: number;
  monthlyContribution: number;
  annualInterestRate: number;
  timePeriod: number;
  timeUnit: TimePeriod;
}

export default function SavingsCalculator() {
  const [savings, setSavings] = useState<SavingsData>({
    principal: 1000,
    monthlyContribution: 100,
    annualInterestRate: 5,
    timePeriod: 5,
    timeUnit: 'years',
  });

  const [results, setResults] = useState<{
    totalInvested: number;
    interestEarned: number;
    finalAmount: number;
    chartData: ChartData<'line'>;
  }>({
    totalInvested: 0,
    interestEarned: 0,
    finalAmount: 0,
    chartData: {
      labels: [],
      datasets: [
        {
          label: 'Total Savings',
          data: [],
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 5,
        },
      ],
    },
  });

  // Calculate savings and update chart
  const calculateSavings = useCallback(() => {
    const { principal, monthlyContribution, annualInterestRate, timePeriod, timeUnit } = savings;

    // Convert time to months for calculation
    const months = timeUnit === 'years' ? timePeriod * 12 : timePeriod;
    const monthlyRate = annualInterestRate / 100 / 12;

    let balance = principal;
    const balances = [balance];
    const labels = ['Start'];

    // Calculate for each year and add intermediate points
    for (let i = 1; i <= months; i++) {
      balance = balance * (1 + monthlyRate) + monthlyContribution;
      if (i % 12 === 0 || i === months) {
        balances.push(parseFloat(balance.toFixed(2)));
        labels.push(`Year ${i / 12}`);
      }
    }

    const totalInvested = principal + monthlyContribution * months;
    const finalAmount = parseFloat(balance.toFixed(2));
    const interestEarned = parseFloat((finalAmount - totalInvested).toFixed(2));

    setResults({
      totalInvested,
      interestEarned,
      finalAmount,
      chartData: {
        labels: labels.map(year => `Year ${year}`),
        datasets: [
          {
            ...results.chartData.datasets[0],
            data: balances,
          },
        ],
      },
    });
  }, [savings]);

  // Calculate on component mount and when inputs change
  useEffect(() => {
    calculateSavings();
  }, [calculateSavings]);

  const handleInputChange = (field: keyof SavingsData, value: string | number) => {
    setSavings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 14, weight: 'normal' },
        bodyFont: { size: 14, weight: 'bold' },
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context) => {
            return ` ${formatCurrency(context.raw as number)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          color: '#6B7280',
          callback: (value) => {
            const num = Number(value);
            if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
            if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
            return `$${num}`;
          },
        },
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 px-2">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 dark:bg-green-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Calculator className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
          </div>
          <ToolPageIntro title="Savings Calculator - Free Online Financial Planning Tool">
                Plan your financial future with our free savings calculator. Calculate compound interest, project savings growth, and achieve your financial goals.
            </ToolPageIntro>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Calculator className="w-5 h-5 mr-2 text-green-500" />
              Input Parameters
            </h2>

            <div className="space-y-5">
              <div>
                <label htmlFor="principal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Initial Investment
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    id="principal"
                    min="0"
                    step="100"
                    value={savings.principal}
                    onChange={(e) => handleInputChange('principal', parseFloat(e.target.value) || 0)}
                    className="block w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="monthlyContribution" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monthly Contribution
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    id="monthlyContribution"
                    min="0"
                    step="10"
                    value={savings.monthlyContribution}
                    onChange={(e) => handleInputChange('monthlyContribution', parseFloat(e.target.value) || 0)}
                    className="block w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="annualInterestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Annual Interest Rate (%)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      id="annualInterestRate"
                      min="0"
                      max="30"
                      step="0.1"
                      value={savings.annualInterestRate}
                      onChange={(e) => handleInputChange('annualInterestRate', parseFloat(e.target.value) || 0)}
                      className="block w-full pl-10 pr-12 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="timePeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time Period
                </label>
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <input
                      type="number"
                      id="timePeriod"
                      min="1"
                      max={savings.timeUnit === 'years' ? '50' : '600'}
                      value={savings.timePeriod}
                      onChange={(e) => handleInputChange('timePeriod', parseInt(e.target.value) || 1)}
                      className="block w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                    />
                  </div>
                  <select
                    value={savings.timeUnit}
                    onChange={(e) => handleInputChange('timeUnit', e.target.value as TimePeriod)}
                    className="block w-24 px-2 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors cursor-pointer"
                  >
                    <option value="years">Years</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-green-500" />
                Projected Savings
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-lg border border-gray-100 dark:border-gray-600">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-300 mb-1">Total Invested</p>
                  <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(results.totalInvested)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-lg border border-gray-100 dark:border-gray-600">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-300 mb-1">Interest Earned</p>
                  <p className="text-lg sm:text-xl font-semibold text-green-500">
                    +{formatCurrency(results.interestEarned)}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-3 sm:p-4 rounded-lg border border-green-100 dark:border-green-800">
                  <p className="text-xs sm:text-sm text-green-700 dark:text-green-300 mb-1">Final Amount</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(results.finalAmount)}
                  </p>
                </div>
              </div>

              <div className="h-64 sm:h-80 w-full bg-white dark:bg-gray-800/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                {results.chartData.labels && results.chartData.labels.length > 0 ? (
                  <Line
                    data={results.chartData}
                    options={chartOptions}
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                    <p>Adjust the inputs to see the chart</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tips Section */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 sm:p-5">
              <h3 className="text-sm sm:text-base font-semibold text-blue-800 dark:text-blue-200 mb-2 sm:mb-3 flex items-center">
                <Info className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                Savings Tips
              </h3>
              <ul className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 space-y-1.5">
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Start saving early to take advantage of compound interest</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Increase your monthly contributions whenever possible</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Consider high-yield savings accounts for better returns</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-1.5">•</span>
                  <span>Automate your savings to stay consistent</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Informational Content */}
        <div className="mt-12">
          <InformationalContent
            title={savingsCalculatorContent.title}
            description={savingsCalculatorContent.description}
            content={savingsCalculatorContent.content}
            faqs={savingsCalculatorContent.faqs}
            relatedTools={savingsCalculatorContent.relatedTools}
          />
        </div>
      </div>
    </div>
  );
}
